import { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { auth, db } from '../../firebase';
import { collection, query, where, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { FiSun, FiMoon, FiMenu, FiBell } from 'react-icons/fi';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { ptBR } from 'date-fns/locale';

const AdminNavbar = ({ toggleSidebar, title, onNotificationClick }) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const user = auth.currentUser;
  
  // Notification states
  const [showNotifications, setShowNotifications] = useState(false);
  const [pendingAppsCount, setPendingAppsCount] = useState(0);
  const [recentPendingApps, setRecentPendingApps] = useState([]);
  const [recentNewUsers, setRecentNewUsers] = useState([]);
  const notificationRef = useRef(null);

  // Close notifications on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Listeners for notifications
  useEffect(() => {
    // Listener for Pending Apps
    // Note: Removed orderBy('createdAt', 'desc') to avoid composite index requirement
    // Sorting will be done client-side
    const adminAppsQuery = query(
      collection(db, 'apps'),
      where('status', 'in', ['pending', 'pending_payment'])
    );

    // Listener for New Users
    const adminUsersQuery = query(
      collection(db, 'users'),
      orderBy('createdAt', 'desc'),
      limit(10)
    );

    const unsubApps = onSnapshot(adminAppsQuery, (snapshot) => {
      const pendingApps = snapshot.docs.map(doc => ({
        id: doc.id,
        type: 'app',
        ...doc.data()
      }));
      // Sort client-side
      pendingApps.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
        return dateB - dateA;
      });
      setRecentPendingApps(pendingApps);
    }, (error) => {
      console.error("Error in apps listener:", error);
    });

    const unsubUsers = onSnapshot(adminUsersQuery, (userSnapshot) => {
      const newUsers = userSnapshot.docs.map(doc => ({
        id: doc.id,
        type: 'user',
        ...doc.data()
      }));
      setRecentNewUsers(newUsers);
    }, (error) => {
      console.error("Error in users listener:", error);
    });

    return () => {
      unsubApps();
      unsubUsers();
    };
  }, []);

  // Calculate total count
  useEffect(() => {
    const notifyUsers = recentNewUsers.filter(u => {
      if (u.isNewUser) return true;
      if (u.userStatus === 'pending') return true;
      if (u.createdAt?.toDate) {
        return (new Date() - u.createdAt.toDate()) < 24 * 60 * 60 * 1000;
      }
      return false;
    });
    setPendingAppsCount(recentPendingApps.length + notifyUsers.length);
  }, [recentPendingApps, recentNewUsers]);

  const handleNotificationItemClick = (item) => {
    setShowNotifications(false);
    if (onNotificationClick) {
      onNotificationClick(item);
    }
  };

  return (
    <div className="flex items-center justify-between mb-6 sm:mb-8 gap-4">
      {/* Botão Hamburger - só aparece no mobile */}
      <div className="flex items-center gap-4">
        <button 
          onClick={toggleSidebar}
          className={`lg:hidden flex-shrink-0 p-2 rounded-xl transition-colors ${isDarkMode ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'}`}
          aria-label="Abrir menu"
        >
          <FiMenu className="w-5 h-5" />
        </button>
        
        <div className="flex-1 min-w-0">
          <h2 className={`text-xl sm:text-2xl lg:text-[28px] font-extrabold tracking-tight leading-tight truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {title || 'Dashboard'}
          </h2>
          {/*
          <p className={`text-sm sm:text-[15px] mt-0.5 truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Área Administrativa do Sistema
          </p>
          */}
        </div>
      </div>

      {/* Right side: Actions & Profile */}
      <div className="flex items-center space-x-2 sm:space-x-4">
        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className={`
            p-2.5 rounded-xl transition-colors
            ${isDarkMode ? 'hover:bg-gray-800 text-yellow-400' : 'hover:bg-gray-100 text-gray-600'}
          `}
          title={isDarkMode ? 'Modo claro' : 'Modo escuro'}
        >
          {isDarkMode ? <FiSun size={20} /> : <FiMoon size={20} />}
        </button>

        {/* Notifications */}
        <div className="relative" ref={notificationRef}>
          <button 
            onClick={() => setShowNotifications(!showNotifications)}
            className={`
              p-2.5 rounded-xl transition-colors relative
              ${isDarkMode ? 'hover:bg-gray-800 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}
              ${showNotifications ? (isDarkMode ? 'bg-gray-800' : 'bg-gray-100') : ''}
            `}
          >
            <FiBell size={20} />
            {pendingAppsCount > 0 && (
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full animate-pulse shadow-sm shadow-red-500/50"></span>
            )}
          </button>

          {/* Dropdown */}
          {showNotifications && (
            <div className={`absolute right-0 mt-3 w-80 sm:w-96 rounded-2xl shadow-xl ring-1 ring-black/5 focus:outline-none overflow-hidden z-50 ${isDarkMode ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-gray-100'}`}>
              <div className={`px-4 py-4 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
                <div className="flex justify-between items-center">
                  <h3 className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Notificações</h3>
                  {pendingAppsCount > 0 && (
                    <span className="px-2.5 py-1 rounded-md text-xs font-bold bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300">
                      {pendingAppsCount} nova{pendingAppsCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>

              <div className="max-h-[350px] overflow-y-auto custom-scrollbar">
                {(recentPendingApps.length > 0 || recentNewUsers.filter(u => u.isNewUser || (u.createdAt?.toDate && (new Date() - u.createdAt.toDate() < 24 * 60 * 60 * 1000))).length > 0) ? (
                  <ul className="divide-y divide-gray-100 dark:divide-gray-800/50">
                    {/* Pending Apps */}
                    {recentPendingApps.map((app) => (
                      <li key={app.id}>
                        <button
                          onClick={() => handleNotificationItemClick(app)}
                          className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/80 transition-colors flex items-start gap-3 group`}
                        >
                          <div className="flex-shrink-0 mt-1">
                            <div className={`w-2 h-2 rounded-full ${app.status === 'pending_payment' ? 'bg-amber-400' : 'bg-green-400'}`}></div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-semibold truncate ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                              {app.name || 'App sem nome'}
                            </p>
                            <p className={`text-xs truncate mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              App • {app.userEmail}
                            </p>
                            <p className={`text-xs mt-1.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              {app.createdAt ? formatDistanceToNow(app.createdAt.toDate(), { addSuffix: true, locale: ptBR }) : 'Recentemente'}
                            </p>
                          </div>
                        </button>
                      </li>
                    ))}

                    {/* New Users */}
                    {recentNewUsers
                      .filter(u => u.isNewUser || u.userStatus === 'pending' || (u.createdAt?.toDate && (new Date() - u.createdAt.toDate() < 24 * 60 * 60 * 1000)))
                      .map((user) => (
                      <li key={user.id}>
                        <button
                          onClick={() => handleNotificationItemClick(user)}
                          className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/80 transition-colors flex items-start gap-3 group`}
                        >
                          <div className="flex-shrink-0 mt-1 relative">
                            {/* Green Blinking Dot for New Users */}
                            <div className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse relative z-10"></div>
                            <div className="absolute top-0 left-0 w-2.5 h-2.5 rounded-full bg-green-500 animate-ping opacity-75"></div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-semibold truncate ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                              Novo Usuário
                            </p>
                            <p className={`text-xs truncate mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {user.name || user.fullName || user.email}
                            </p>
                            <p className={`text-xs mt-1.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              {user.createdAt ? formatDistanceToNow(user.createdAt.toDate(), { addSuffix: true, locale: ptBR }) : 'Recentemente'}
                            </p>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="px-4 py-8 text-center">
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      Nenhuma notificação nova
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className={`h-8 w-px hidden sm:block ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`}></div>

        {/* User Info */}
        <div className="flex items-center space-x-3">
          {/* Ocultado para dar mais espaço
          <div className="text-right hidden sm:block">
            <p className="text-sm font-bold leading-none">{user?.email}</p>
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Administrador</p>
          </div>
          */}
          <div className="h-10 w-10 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm shadow-sm shadow-violet-500/20">
            {user?.email?.charAt(0).toUpperCase()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminNavbar;
